// src/components/Admin/Payment.js
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';

const Payment = () => {
  const { currentUser, updateCurrentUser } = useContext(AuthContext);
  const MAX_TIP_OPTIONS = 5;
  const defaultTipOptions = [
    { id: 1, value: "$0", removable: false },
    { id: 2, value: "$5", removable: true },
    { id: 3, value: "$25", removable: true },
    { id: 4, value: "$50", removable: true },
    { id: 5, value: "Custom", removable: true },
  ];

  // Initialize tippingEnabled to false so it doesn't show enabled by default.
  const [tipOptions, setTipOptions] = useState(defaultTipOptions);
  const [tippingEnabled, setTippingEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stripeStatusMessage, setStripeStatusMessage] = useState('');

  // Load tip options and tippingEnabled once when the component mounts.
  useEffect(() => {
    if (currentUser) {
      fetch(`/api/payments/tipOptions?userId=${currentUser._id}`)
        .then(res => res.json())
        .then(data => {
          if (data.tipOptions) {
            setTipOptions(data.tipOptions);
          }
          // Initialize the toggle state from backend once.
          if (typeof data.tippingEnabled !== 'undefined') {
            setTippingEnabled(data.tippingEnabled);
          }
        })
        .catch(() => {
          setTipOptions(defaultTipOptions);
          setTippingEnabled(false);
        });
    }
  }, []); // Run only once on mount

  // Fetch and update Stripe account status for the current user
  useEffect(() => {
    if (currentUser?._id && currentUser?.stripeAccountId) {
      fetch(`/api/payments/account-status?userId=${currentUser._id}`)
        .then(async (res) => {
          const data = await res.json();
          if (!res.ok) {
            throw data;
          }
          return data;
        })
        .then(data => {
          console.log("Stripe account status:", data);
          setStripeStatusMessage(data.message || '');
          updateCurrentUser({
            ...currentUser,
            stripeAccountStatus: data.stripeAccountStatus,
            stripeConnectMessage: data.message || '',
          });
        })
        .catch(err => {
          console.error("Error fetching account status:", err);
          const message = err?.message || 'Unable to load Stripe account status.';
          setStripeStatusMessage(message);

          if (err?.stripeAccountStatus) {
            updateCurrentUser({
              ...currentUser,
              stripeAccountStatus: err.stripeAccountStatus,
              stripeConnectMessage: message,
            });
          }
        });
    }
  }, [currentUser?._id, currentUser?.stripeAccountId]);

  // Save tip options and tippingEnabled settings
  const handleSaveTipOptions = () => {
    setSaving(true);
    fetch('/api/payments/tipOptions', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        userId: currentUser._id, 
        tipOptions,
        tippingEnabled
      })
    })
      .then(res => res.json())
      .then(data => {
        if (data.tipOptions) {
          setTipOptions(data.tipOptions);
        }
        if (typeof data.tippingEnabled !== 'undefined') {
          setTippingEnabled(data.tippingEnabled);
        }
        setSaving(false);
        alert('Payment options saved successfully!');
      })
      .catch(err => {
        console.error('Error saving tip options:', err);
        setSaving(false);
        alert('Failed to save payment options.');
      });
  };

  const handleRemoveTipOption = (id) => {
    setTipOptions(prevOptions => prevOptions.filter(option => option.id !== id));
  };

  const handleAddTipOption = () => {
    if (tipOptions.length < MAX_TIP_OPTIONS) {
      const newId = tipOptions.length > 0 ? Math.max(...tipOptions.map(o => o.id)) + 1 : 1;
      const newOption = { id: newId, value: "Custom", removable: true };
      setTipOptions(prevOptions => [...prevOptions, newOption]);
    }
  };

  const handleTipOptionChange = (id, newValue) => {
    setTipOptions(prevOptions =>
      prevOptions.map(option =>
        option.id === id ? { ...option, value: newValue } : option
      )
    );
  };

  // Trigger Stripe Connect onboarding with IP-based country detection
  const handleConnectStripe = async () => {
    if (!currentUser) {
      console.error("No current user available");
      alert("You must be logged in to set up your Stripe account.");
      return;
    }
    try {
      const geoResponse = await fetch('https://ipapi.co/json/');
      const geoData = await geoResponse.json();
      const userCountry = geoData && geoData.country ? geoData.country : "CA";
      const res = await fetch('/api/payments/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: currentUser._id, country: userCountry })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.message || 'Error connecting your Stripe account.');
        return;
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        alert('Error connecting your Stripe account.');
      }
    } catch (error) {
      console.error('Error connecting Stripe account:', error);
      alert('Error connecting your Stripe account.');
    }
  };

  const stripeButtonText = currentUser && currentUser.stripeAccountId
    ? "Edit Stripe account"
    : "Connect Stripe account";

  // Handler for toggling tippingEnabled that checks account completion
  const handleTippingEnabledChange = (e) => {
    const newValue = e.target.checked;
    if (newValue) {
      if (!currentUser || !currentUser.stripeAccountId) {
        console.log("Tipping toggle disabled: No Stripe Connect account found.");
        alert("Please set up your Stripe Connect account first.");
        return;
      }
      if (!currentUser.stripeAccountStatus || currentUser.stripeAccountStatus !== 'completed') {
        console.log("Tipping toggle disabled: Stripe Connect account not complete. Current status:", currentUser.stripeAccountStatus);
        alert("Please complete your Stripe Connect account before enabling tipping.");
        return;
      }
      console.log("Tipping toggle enabled: Stripe Connect account is connected and completed.");
    }
    setTippingEnabled(newValue);
  };

  return (
    <div className="row spacing_s">
      <div className="col-xs-12">
        <div className="card form">
          {/* Enabling Tipping */}
          <div className="row spacing_s margin-top-s">
            <div className="col-xs-12">
              <div className="text-uppercase margin-bottom-m">
                Enabling Tipping
              </div>
              <label className="switch">
                <input 
                  type="checkbox" 
                  checked={tippingEnabled} 
                  onChange={handleTippingEnabledChange} 
                />
                <span className="slider round" />
              </label>
            </div>
          </div>

          {/* Tip Options Section */}
          {tippingEnabled && (
            <div className="row spacing_s top-xs">
              {tipOptions.map((option, index) => (
                <div className="col-xs-12 col-sm-6 col-md-4 col-lg" key={option.id}>
                  <div className="text-uppercase">
                    {`Tip amount Option ${index + 1}`}
                  </div>
                  <select
                    value={option.value}
                    onChange={(e) => handleTipOptionChange(option.id, e.target.value)}
                  >
                    <option>$0</option>
                    <option>$1</option>
                    <option>$5</option>
                    <option>$10</option>
                    <option>$25</option>
                    <option>$50</option>
                    <option>$100</option>
                    <option>$250</option>
                    <option>$500</option>
                    <option>$1000</option>
                    <option>Custom</option>
                  </select>
                  {option.removable && (
                    <button
                      className="text-link margin-top-m"
                      onClick={() => handleRemoveTipOption(option.id)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add Tip Option Button */}
          {tippingEnabled && (
            <div className="row spacing_s margin-bottom-xl">
              <div className="col-xs-12">
                <button
                  className="small line"
                  onClick={handleAddTipOption}
                  disabled={tipOptions.length >= MAX_TIP_OPTIONS}
                >
                  Add tip option
                </button>
              </div>
            </div>
          )}

          {/* Payout Method */}
          <div className="row spacing_s">
            <div className="col-xs-12">
              <div className="text-uppercase margin-bottom-m">
                Payout Method
              </div>
              <button className="small line" onClick={handleConnectStripe}>
                {stripeButtonText}
              </button>
              {(stripeStatusMessage || currentUser?.stripeConnectMessage) && (
                <div className="margin-top-m">
                  {stripeStatusMessage || currentUser?.stripeConnectMessage}
                </div>
              )}
            </div>
          </div>

          {/* Save Payment Options */}
          <div className="row spacing_s">
            <div className="col-xs-12">
              <button className="small line" onClick={handleSaveTipOptions} disabled={saving}>
                Save Payment Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;
